import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { defaultSiteDescription, useSeoMeta } from '../hooks/useSeoMeta.ts';
import './css/HomeEditorialPage.css';

const SLIDE_COUNT = 2;
const SLIDE_LOCK_MS = 780;

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function OrbitVisual() {
  return (
    <div className="home-ed-orbit" aria-hidden>
      <svg className="home-ed-orbit-svg" viewBox="0 0 520 420" fill="none">
        <ellipse className="home-ed-orbit-ring" cx="250" cy="210" rx="168" ry="62" transform="rotate(-18 250 210)" />
        <ellipse className="home-ed-orbit-ring home-ed-orbit-ring--soft" cx="250" cy="210" rx="210" ry="78" transform="rotate(-18 250 210)" />
        <ellipse className="home-ed-orbit-ring" cx="250" cy="210" rx="132" ry="200" />
        <line className="home-ed-orbit-axis" x1="250" y1="18" x2="250" y2="402" />
        <line className="home-ed-orbit-axis" x1="48" y1="210" x2="452" y2="210" />
        <circle className="home-ed-orbit-dot home-ed-orbit-dot--mute" cx="168" cy="248" r="3.2" />
        <circle className="home-ed-orbit-dot home-ed-orbit-dot--mute" cx="312" cy="132" r="3.2" />
        <circle className="home-ed-orbit-dot home-ed-orbit-dot--accent" cx="402" cy="168" r="5.5" />
      </svg>
      <p className="home-ed-orbit-caption">
        <span>PRACTICE</span>
        <span>BUILDS</span>
        <span>UNDERSTANDING</span>
      </p>
    </div>
  );
}

type GlyphName =
  | 'sql'
  | 'algo'
  | 'pi'
  | 'atom'
  | 'bars'
  | 'dna'
  | 'pulse'
  | 'torus'
  | 'cases'
  | 'cube'
  | 'link'
  | 'terminal';

const stroke = {
  stroke: 'currentColor',
  strokeWidth: 1.4,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function Glyph({ name }: { name: GlyphName }) {
  let body: ReactNode = null;
  if (name === 'sql') {
    body = (
      <>
        <ellipse cx="9" cy="4.5" rx="5" ry="2" {...stroke} />
        <path d="M4 4.5v8c0 1.2 2.2 2.2 5 2.2s5-1 5-2.2v-8" {...stroke} />
        <path d="M4 8.5c0 1.2 2.2 2.2 5 2.2s5-1 5-2.2" {...stroke} />
      </>
    );
  } else if (name === 'algo') {
    body = (
      <>
        <circle cx="4.6" cy="12.4" r="1.45" {...stroke} />
        <circle cx="12.8" cy="5" r="1.45" {...stroke} />
        <circle cx="13.4" cy="13.1" r="1.45" {...stroke} />
        <path d="M5.9 11.5 11.4 6.1M6 12.5h6" {...stroke} />
      </>
    );
  } else if (name === 'pi') {
    body = (
      <text
        x="9"
        y="13.2"
        textAnchor="middle"
        fill="currentColor"
        fontSize="14"
        fontFamily="Georgia, 'Times New Roman', serif"
      >
        π
      </text>
    );
  } else if (name === 'atom') {
    body = (
      <>
        <circle cx="9" cy="9" r="1.15" fill="currentColor" />
        <ellipse cx="9" cy="9" rx="6.2" ry="2.35" {...stroke} />
        <ellipse cx="9" cy="9" rx="6.2" ry="2.35" transform="rotate(60 9 9)" {...stroke} />
        <ellipse cx="9" cy="9" rx="6.2" ry="2.35" transform="rotate(120 9 9)" {...stroke} />
      </>
    );
  } else if (name === 'bars') {
    body = <path d="M4 14V9.2M9 14V4.4M14 14V7.6" {...stroke} />;
  } else if (name === 'dna') {
    body = (
      <>
        <path d="M6.1 2.6c3 2 3 3.1 0 5.1s-3 3.1 0 5.1" {...stroke} />
        <path d="M11.9 2.6c-3 2-3 3.1 0 5.1s3 3.1 0 5.1" {...stroke} />
        <path d="M7.3 6.1h3.4M7.3 9h3.4M7.3 11.9h3.4" {...stroke} />
      </>
    );
  } else if (name === 'pulse') {
    body = <path d="M1.8 9.2h3.1L6.8 4.6l2.7 8.4 1.7-3.8h5" {...stroke} />;
  } else if (name === 'torus') {
    body = (
      <>
        <ellipse cx="9" cy="9" rx="5.5" ry="2.25" {...stroke} />
        <ellipse cx="9" cy="9" rx="2" ry="1.05" {...stroke} />
      </>
    );
  } else if (name === 'cases') {
    body = (
      <>
        <circle cx="5" cy="5.4" r="1.35" {...stroke} />
        <circle cx="13" cy="4.8" r="1.35" {...stroke} />
        <circle cx="9" cy="13.2" r="1.35" {...stroke} />
        <path d="M6.2 6 11.8 5.3M5.7 6.6 8.2 12M12.3 6 9.8 12" {...stroke} />
      </>
    );
  } else if (name === 'cube') {
    body = (
      <>
        <path d="M9 2.8 14.5 5.8v6.4L9 15.2 3.5 12.2V5.8L9 2.8Z" {...stroke} />
        <path d="M9 9v6.2M3.5 5.8 9 9l5.5-3.2" {...stroke} />
      </>
    );
  } else if (name === 'link') {
    body = (
      <>
        <circle cx="6" cy="9" r="2.25" {...stroke} />
        <circle cx="12" cy="9" r="2.25" {...stroke} />
        <path d="M8.1 9h1.8" {...stroke} />
      </>
    );
  } else {
    body = (
      <>
        <path d="M4 5.6 8 9l-4 3.4" {...stroke} />
        <path d="M9.6 12.4H14" {...stroke} />
      </>
    );
  }

  return (
    <svg className="home-ed-glyph" viewBox="0 0 18 18" fill="none" aria-hidden>
      {body}
    </svg>
  );
}

const SKY_NODES: Array<{
  id: string;
  icon: GlyphName;
  labelKey: string;
  x: number;
  y: number;
  xs: number;
  ys: number;
  stacked?: boolean;
}> = [
  { id: 'sql', icon: 'sql', labelKey: 'homePage.editorialSkySql', x: 50, y: 16, xs: 50, ys: 9, stacked: true },
  { id: 'algo', icon: 'algo', labelKey: 'homePage.editorialSkyAlgo', x: 26, y: 28, xs: 22, ys: 25 },
  { id: 'math', icon: 'pi', labelKey: 'homePage.editorialSkyMath', x: 74, y: 28, xs: 78, ys: 25 },
  { id: 'physics', icon: 'atom', labelKey: 'homePage.editorialSkyPhysics', x: 16, y: 50, xs: 18, ys: 44 },
  { id: 'data', icon: 'bars', labelKey: 'homePage.editorialSkyData', x: 82, y: 50, xs: 82, ys: 44 },
  { id: 'bio', icon: 'dna', labelKey: 'homePage.editorialSkyBio', x: 24, y: 70, xs: 22, ys: 63 },
  { id: 'med', icon: 'pulse', labelKey: 'homePage.editorialSkyMed', x: 76, y: 70, xs: 78, ys: 63 },
  { id: 'tda', icon: 'torus', labelKey: 'homePage.editorialSkyTda', x: 36, y: 84, xs: 28, ys: 84 },
  { id: 'cases', icon: 'cases', labelKey: 'homePage.editorialSkyCases', x: 64, y: 84, xs: 72, ys: 84 },
];

const BREADTH_POINTS: Array<{ icon: GlyphName; titleKey: string; textKey: string }> = [
  {
    icon: 'cube',
    titleKey: 'homePage.editorialBreadthPoint1Title',
    textKey: 'homePage.editorialBreadthPoint1Text',
  },
  {
    icon: 'link',
    titleKey: 'homePage.editorialBreadthPoint2Title',
    textKey: 'homePage.editorialBreadthPoint2Text',
  },
  {
    icon: 'bars',
    titleKey: 'homePage.editorialBreadthPoint3Title',
    textKey: 'homePage.editorialBreadthPoint3Text',
  },
];

const COMPARE_PLAIN = [
  'homePage.editorialComparePlain1',
  'homePage.editorialComparePlain2',
  'homePage.editorialComparePlain3',
  'homePage.editorialComparePlain4',
] as const;

const COMPARE_BRAND = [
  'homePage.editorialCompareBrand1',
  'homePage.editorialCompareBrand2',
  'homePage.editorialCompareBrand3',
  'homePage.editorialCompareBrand4',
] as const;

function BreadthSky({ t }: { t: (key: string) => string }) {
  return (
    <div className="home-ed-sky" aria-hidden>
      <svg className="home-ed-sky-svg" viewBox="0 0 100 100">
        <ellipse className="home-ed-sky-ring" cx="50" cy="50" rx="33" ry="33" />
        <ellipse
          className="home-ed-sky-ring home-ed-sky-ring--dim"
          cx="50"
          cy="50"
          rx="44"
          ry="16"
          transform="rotate(-18 50 50)"
        />
        <ellipse
          className="home-ed-sky-ring home-ed-sky-ring--dim"
          cx="50"
          cy="50"
          rx="18"
          ry="42"
          transform="rotate(16 50 50)"
        />
        {SKY_NODES.map((node) => (
          <line key={node.id} className="home-ed-sky-line" x1="50" y1="50" x2={node.x} y2={node.y} />
        ))}
        <circle className="home-ed-sky-dot" cx="31" cy="36" r="0.7" />
        <circle className="home-ed-sky-dot" cx="64" cy="34" r="0.5" />
        <circle className="home-ed-sky-dot" cx="73" cy="60" r="0.55" />
        <circle className="home-ed-sky-dot home-ed-sky-dot--bright" cx="39" cy="64" r="0.85" />
      </svg>
      <span className="home-ed-sky-orb home-ed-sky-orb--a" />
      <span className="home-ed-sky-orb home-ed-sky-orb--b" />
      <span className="home-ed-sky-orb home-ed-sky-orb--c" />
      <div className="home-ed-sky-core">
        <span className="home-ed-sky-brand">Sciplex</span>
      </div>
      {SKY_NODES.map((node) => (
        <div
          key={node.id}
          className={`home-ed-sky-node${node.stacked ? ' is-stacked' : ''}`}
          style={{
            ['--node-x' as string]: `${node.x}%`,
            ['--node-y' as string]: `${node.y}%`,
            ['--node-xs' as string]: `${node.xs}%`,
            ['--node-ys' as string]: `${node.ys}%`,
          }}
        >
          <Glyph name={node.icon} />
          <span>{t(node.labelKey)}</span>
        </div>
      ))}
    </div>
  );
}

function HomeEditorialPage() {
  const { t, currentLanguage } = useTranslation();
  const [slide, setSlide] = useState(0);
  const lockUntil = useRef(0);
  const touchStartY = useRef<number | null>(null);
  const breadthScrollRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef(0);
  slideRef.current = slide;

  useSeoMeta({
    title:
      currentLanguage === 'ru'
        ? 'Sciplex — задачи по программированию, SQL и алгоритмам онлайн'
        : 'Sciplex — programming, SQL and algorithm practice online',
    description: defaultSiteDescription(currentLanguage),
    canonicalPath: '/',
  });

  const goTo = useCallback((next: number) => {
    const clamped = Math.max(0, Math.min(SLIDE_COUNT - 1, next));
    setSlide((current) => (current === clamped ? current : clamped));
  }, []);

  const tryMove = useCallback(
    (delta: number) => {
      const now = Date.now();
      if (now < lockUntil.current) return;
      setSlide((current) => {
        const next = Math.max(0, Math.min(SLIDE_COUNT - 1, current + delta));
        if (next !== current) {
          lockUntil.current = now + (prefersReducedMotion() ? 120 : SLIDE_LOCK_MS);
        }
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    const breadthConsumes = (delta: number) => {
      const el = breadthScrollRef.current;
      if (slideRef.current !== 1 || !el || delta === 0) return false;
      if (el.scrollHeight <= el.clientHeight + 4) return false;
      if (delta > 0) return el.scrollTop + el.clientHeight < el.scrollHeight - 4;
      return el.scrollTop > 4;
    };

    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) < 12) return;
      if (breadthConsumes(event.deltaY)) return;
      event.preventDefault();
      tryMove(event.deltaY > 0 ? 1 : -1);
    };
    const onKey = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const down = event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ';
      const up = event.key === 'ArrowUp' || event.key === 'PageUp';
      if (!down && !up) return;
      event.preventDefault();
      const delta = down ? 1 : -1;
      if (breadthConsumes(delta)) {
        breadthScrollRef.current?.scrollBy({
          top: delta * 96,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
        return;
      }
      tryMove(delta);
    };
    const onTouchStart = (event: TouchEvent) => {
      touchStartY.current = event.touches[0]?.clientY ?? null;
    };
    const onTouchEnd = (event: TouchEvent) => {
      const start = touchStartY.current;
      const end = event.changedTouches[0]?.clientY;
      touchStartY.current = null;
      if (start == null || end == null) return;
      const delta = start - end;
      if (Math.abs(delta) < 48) return;
      if (breadthConsumes(delta)) return;
      tryMove(delta > 0 ? 1 : -1);
    };

    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    window.addEventListener('keydown', onKey, { capture: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
    return () => {
      window.removeEventListener('wheel', onWheel, { capture: true });
      window.removeEventListener('keydown', onKey, { capture: true });
      window.removeEventListener('touchstart', onTouchStart, { capture: true });
      window.removeEventListener('touchend', onTouchEnd, { capture: true });
    };
  }, [tryMove]);

  return (
    <div className="home-ed" data-slide={slide}>
      <div
        className="home-ed-track"
        style={{ transform: `translate3d(0, -${slide * 100}%, 0)` }}
      >
        <section className="home-ed-slide home-ed-slide--hero">
          <div className="home-ed-hero-grid">
            <div className="home-ed-copy">
              <h1 className="home-ed-wordmark" aria-label="Sciplex">
                Sc<span className="home-ed-wordmark-i">i</span>plex
              </h1>
              <p className="home-ed-lead">{t('homePage.editorialLead')}</p>
            </div>
            <OrbitVisual />
          </div>
          <div className="home-ed-index">
            <span>01</span>
            <span className="home-ed-index-rule" />
            <span>{t('homePage.editorialIndex')}</span>
          </div>
        </section>

        <section className="home-ed-slide home-ed-slide--breadth" aria-labelledby="home-ed-breadth-title">
          <div className="home-ed-breadth-scroll" ref={breadthScrollRef}>
            <div className="home-ed-breadth">
              <div className="home-ed-breadth-copy">
                <p className="home-ed-kicker">{t('homePage.editorialBreadthKicker')}</p>
                <h2 id="home-ed-breadth-title" className="home-ed-breadth-title">
                  <span className="home-ed-breadth-title-line">
                    {t('homePage.editorialBreadthTitleBefore')}{' '}
                    <span className="home-ed-breadth-title-accent">
                      {t('homePage.editorialBreadthTitleAccent')}
                    </span>
                  </span>
                  <span className="home-ed-breadth-title-line">
                    {t('homePage.editorialBreadthTitleAfter')}
                  </span>
                </h2>
                <p className="home-ed-breadth-lead">{t('homePage.editorialBreadthLead')}</p>
                <ul className="home-ed-breadth-points">
                  {BREADTH_POINTS.map((point) => (
                    <li key={point.titleKey} className="home-ed-breadth-point">
                      <h3>
                        <Glyph name={point.icon} />
                        <span>{t(point.titleKey)}</span>
                      </h3>
                      <p>{t(point.textKey)}</p>
                    </li>
                  ))}
                </ul>
                <div className="home-ed-vs">
                  <article className="home-ed-vs-card">
                    <h3 className="home-ed-vs-head">
                      <span className="home-ed-vs-mark">
                        <Glyph name="terminal" />
                      </span>
                      <span>{t('homePage.editorialComparePlainTitle')}</span>
                    </h3>
                    <ul className="home-ed-vs-list">
                      {COMPARE_PLAIN.map((key) => (
                        <li key={key}>
                          <span className="home-ed-vs-x" aria-hidden>
                            ×
                          </span>
                          <span>{t(key)}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                  <p className="home-ed-vs-badge">VS</p>
                  <article className="home-ed-vs-card home-ed-vs-card--brand">
                    <h3 className="home-ed-vs-head home-ed-vs-head--brand">Sciplex</h3>
                    <ul className="home-ed-vs-list">
                      {COMPARE_BRAND.map((key) => (
                        <li key={key}>
                          <span className="home-ed-vs-ok" aria-hidden>
                            ✓
                          </span>
                          <span>{t(key)}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                </div>
              </div>
              <div className="home-ed-breadth-visual">
                <div className="home-ed-sky-slot">
                  <BreadthSky t={t} />
                </div>
                <div className="home-ed-breadth-meta">
                  <p>{t('homePage.editorialBreadthFooter')}</p>
                  <span>02</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="home-ed-pager" aria-hidden>
        {Array.from({ length: SLIDE_COUNT }, (_, index) => (
          <button
            key={index}
            type="button"
            className={`home-ed-pager-dot${index === slide ? ' is-active' : ''}`}
            onClick={() => goTo(index)}
            aria-label={index === 0 ? t('homePage.editorialIndex') : t('homePage.editorialBreadthKicker')}
          />
        ))}
      </div>
    </div>
  );
}

export default HomeEditorialPage;
